@file:Suppress("DEPRECATION")

package org.example.newportfolio.components.sections.footer

import androidx.compose.runtime.Composable
import org.example.newportfolio.theme.fonts.LabelLargeTextStyle
import org.example.newportfolio.theme.fonts.TextStyle
import org.example.newportfolio.theme.fonts.TextStyleSecondaryColor
import org.example.newportfolio.utils.Res
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.style.ComponentStyle
import com.varabyte.kobweb.silk.components.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.components.style.toModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.palette.overlay
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px

val FooterStyle by ComponentStyle {
    val colorPalette = colorMode.toPalette()
    base {
        Modifier
            .fillMaxWidth()
            .background(colorPalette.overlay)
            // Subtle brand-tinted hairline so the footer reads as a distinct
            // closing band (the redundant social-icon row was removed).
            .styleModifier {
                property("border-top", "1px solid rgba(159, 52, 240, 0.14)")
            }
    }
    Breakpoint.ZERO {
        // bottom 90px reserves the chatbot FAB envelope (20px inset + 56px) plus
        // breathing room so the powered-by line never tucks under the bubble.
        Modifier.padding(left = 16.px, right = 16.px, top = 28.px, bottom = 90.px)
    }
    Breakpoint.SM {
        Modifier.padding(left = 24.px, right = 24.px, top = 28.px, bottom = 90.px)
    }
    Breakpoint.MD {
        Modifier.padding(left = 34.px, right = 34.px, top = 34.px, bottom = 34.px)
    }
    Breakpoint.LG {
        Modifier.padding(left = 54.px, right = 54.px, top = 40.px, bottom = 40.px)
    }
    Breakpoint.XL {
        Modifier.padding(left = 104.px, right = 104.px, top = 40.px, bottom = 40.px)
    }
}

@Composable
fun Footer() {
    val bp = rememberBreakpoint()
    // Minimal, truly-centered closing band. Two prior bugs are fixed here:
    //  (1) the asymmetric right=80px / left=12px padding that shifted the whole
    //      block off-centre is gone — symmetric padding now comes from
    //      FooterStyle, so horizontalAlignment = Center actually centers.
    //  (2) the single "© … · Powered by Kotlin" Row was wider than a phone and
    //      wrapped mid-word into a two-line mess; on ZERO it now stacks
    //      vertically with no dot. The SM+ single-line row keeps the dot.
    // The footer social icons were removed — the contact card directly above
    // already shows the same four, at a larger size.
    Column(
        modifier = FooterStyle.toModifier()
            .gap(if (bp < Breakpoint.SM) 0.4.cssRem else 0.75.cssRem),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        if (bp < Breakpoint.SM) {
            Copyright()
            PoweredByKobweb()
        } else {
            Row(
                modifier = Modifier.gap(0.6.em),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Center
            ) {
                Copyright()
                SpanText(
                    "·",
                    modifier = TextStyle.toModifier(LabelLargeTextStyle, TextStyleSecondaryColor)
                )
                PoweredByKobweb()
            }
        }
    }
}

@Composable
private fun Copyright() {
    SpanText(
        modifier = TextStyle.toModifier(LabelLargeTextStyle, TextStyleSecondaryColor)
            .textAlign(TextAlign.Center),
        text = Res.Strings.COPYRIGHT
    )
}

@Composable
private fun PoweredByKobweb() {
    Row(verticalAlignment = Alignment.CenterVertically) {
        SpanText(
            modifier = TextStyle.toModifier(LabelLargeTextStyle, TextStyleSecondaryColor)
                .textAlign(TextAlign.Center),
            text = "Powered by "
        )
        SpanText(
            modifier = TextStyle.toModifier(LabelLargeTextStyle, TextStyleSecondaryColor)
                .textAlign(TextAlign.Center),
            text = "Kotlin"
        )
        Image(
            src = Res.Images.KOTLIN,
            alt = "Kotlin programming language logo",
            modifier = Modifier
                .display(DisplayStyle.Flex)
                .height(1.25.cssRem)
                .margin(left = 0.375.cssRem)
        )
    }
}
